import { useLocation } from 'preact-iso';
import { api } from '../../api/client';
import type { NotificationSeverity, NotificationView } from '../../api/types';
import { useMe } from '../../auth/useMe';
import { useInbox } from '../../hooks/useInbox';
import { severityStyle } from './severity';

/**
 * Top-of-shell alert banner (ADR-0028 §4). Renders the Active+Banner
 * subset of the shared inbox poll. A sticky operational condition (e.g.
 * "GitHub token rejected") stays until its producer resolves it - it
 * carries no dismiss control; a one-shot dismissible banner shows an x
 * that upserts the caller's dismiss receipt.
 *
 * Stacking is capped so a bad day never buries the page: the top two by
 * severity render, and any remainder collapses into a "+N more" hint that
 * points the operator at the bell. The steady state is zero or one banner.
 */
const SEVERITY_RANK: Record<NotificationSeverity, number> = {
    Error: 0,
    Warning: 1,
    Info: 2,
};

const MAX_VISIBLE = 2;

export function AlertBanner() {
    const me = useMe();
    const { inbox, refresh } = useInbox();
    const location = useLocation();
    const { banners } = inbox;

    if (me.status !== 'authenticated' || banners.length === 0) return null;

    const ordered = [...banners].sort(
        (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity],
    );
    const visible = ordered.slice(0, MAX_VISIBLE);
    const overflow = ordered.length - visible.length;

    const onDismiss = (n: NotificationView) => {
        void api.notifications.dismiss(n.id).then(refresh).catch(() => undefined);
    };

    const onCta = (n: NotificationView) => {
        if (!n.link) return;
        if (n.link.startsWith('/')) location.route(n.link);
        else if (typeof window !== 'undefined') window.location.assign(n.link);
    };

    return (
        <div class="flex flex-col gap-px">
            {visible.map((n) => {
                const style = severityStyle(n.severity);
                return (
                    <div
                        key={n.id}
                        role="alert"
                        class={`border-b px-[clamp(20px,3.5vw,56px)] py-3 ${style.banner}`}
                    >
                        <div class="mx-auto flex max-w-[1680px] items-center gap-4">
                            <span
                                aria-hidden="true"
                                class={`h-2 w-2 flex-none rounded-full ${style.dot}`}
                            />
                            <div class="min-w-0 flex-1">
                                <p class={`text-[13.5px] font-medium ${style.text}`}>
                                    {n.title}
                                </p>
                                {n.description && (
                                    <p class="mt-0.5 text-[12.5px] text-fg-secondary">
                                        {n.description}
                                    </p>
                                )}
                            </div>
                            {n.link && (
                                <button
                                    type="button"
                                    onClick={() => onCta(n)}
                                    class={`flex-none rounded-full border border-current px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] transition-opacity hover:opacity-75 ${style.text}`}
                                >
                                    {n.linkLabel ?? 'Fix'}
                                </button>
                            )}
                            {n.dismissible && (
                                <button
                                    type="button"
                                    onClick={() => onDismiss(n)}
                                    aria-label="Dismiss"
                                    class="grid h-6 w-6 flex-none place-items-center rounded text-fg-muted transition-colors hover:bg-surface-base hover:text-fg-primary"
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        aria-hidden="true"
                                    >
                                        <path d="M18 6 6 18" />
                                        <path d="m6 6 12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}

            {overflow > 0 && (
                <div class="border-b border-rule bg-surface-base/60 px-[clamp(20px,3.5vw,56px)] py-1.5">
                    <p class="mx-auto max-w-[1680px] text-[11.5px] text-fg-muted">
                        +{overflow} more alert{overflow === 1 ? '' : 's'} - see the
                        notification bell.
                    </p>
                </div>
            )}
        </div>
    );
}
